import {
	getGetMeQueryKey,
	useUpdateMe,
} from '@cocoonspace/sdk-js/domains/profile/profile'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '~/components/ui/button'
import { Form } from '~/components/ui/form'
import { FormControlledInput } from '~/components/ui/form-controlled-input'
import { Input } from '~/components/ui/input'
import { useTracking } from '~/providers/tracking-provider'
import { useUserContext } from '~/providers/user-provider'

export type InfosPersoFormValues = z.infer<typeof schema>

const nonemptyMessage = 'errors:forms.required'

const schema = z.object({
	last_name: z.string().nonempty({ message: nonemptyMessage }),
	first_name: z.string().nonempty({ message: nonemptyMessage }),
	phone: z.string().nonempty({ message: nonemptyMessage }),
})

export const InfosPersoForm = ({ onSuccess }: { onSuccess?: VoidFunction }) => {
	const router = useRouter()
	const queryClient = useQueryClient()
	const { t } = useTranslation()
	const { track } = useTracking()
	const { userQuery } = useUserContext()
	const updateUser = useUpdateMe()

	const form = useForm<InfosPersoFormValues>({
		resolver: zodResolver(schema),
		values: {
			last_name: userQuery.data?.last_name || '',
			first_name: userQuery.data?.first_name || '',
			phone: userQuery.data?.phone || '',
		},
	})

	const onSubmit = async (formValues: InfosPersoFormValues) => {
		const query = updateUser.mutateAsync({ data: formValues }).then(() => {
			track.personalInfosUpdate(router.route)

			queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() })
			onSuccess?.()
		})

		toast.promise(query, {
			loading: t('infosPersoForm:loadingMessage'),
			success: t('infosPersoForm:successMessage'),
			error: (err: any) =>
				t([
					`errors:api.${err.response?.data?.errors[0].code}`,
					'errors:generic',
				]),
		})
	}

	return (
		<form
			id='personal-informations-form'
			noValidate
			className='flex flex-1 flex-col'
			onSubmit={form.handleSubmit(onSubmit)}
		>
			<Form {...form}>
				<FormControlledInput
					name='first_name'
					label={t('infosPersoForm:first_name')}
					control={form.control}
					render={({ field }) => <Input {...field} />}
					required
				/>

				<FormControlledInput
					name='last_name'
					label={t('infosPersoForm:last_name')}
					control={form.control}
					render={({ field }) => <Input {...field} />}
					required
				/>

				<FormControlledInput
					name='phone'
					label={t('infosPersoForm:phone')}
					control={form.control}
					render={({ field }) => <Input {...field} />}
					required
				/>

				<Button
					data-testid=''
					disabled={updateUser.isLoading}
					type='submit'
					className='mt-4 w-full md:mt-auto'
				>
					{t('infosPersoForm:validate')}
				</Button>
			</Form>
		</form>
	)
}
